import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormEditor from './FormEditor';

const Component = ({ value, onChange, editor, disabled, errors, readOnly, data }) => {
  const { id, name, label } = editor;
  const {
    type,
    min,
    max,
    dense,
    autoComplete,
    startAdornment,
    endAdornment,
    margin = 'normal',
    multiline,
    fullWidth,
    description,
    minWidth,
    maxWidth,
    required,
  } = editor.property;

  const handleChange = (event) => {
    let newValue = event.target.value;
    if (type === 'integer') {
      try {
        newValue = parseInt(newValue, 10);
      } catch (e) {
        console.error('Invalid intger value ', value);
        console.error(e);
        newValue = value;
      }
    } else if (type === 'number') {
      try {
        newValue = parseFloat(newValue);
      } catch (e) {
        console.error(e);
        newValue = value;
      }
    }
    onChange(name, newValue);
  };

  const error = errors && errors.length && errors[0];
  const numeric = (type === 'number' || type === 'integer');
  const InputProps = {
    readOnly: Boolean(readOnly === undefined ? false : readOnly),
    startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
    endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
    inputProps: (type === 'integer' || type === 'number')
          ? { style: { textAlign: 'right' } } : undefined,
  };
  if (((min !== undefined) || (max !== undefined)) && numeric) {
    InputProps.inputProps = Object.assign({}, InputProps.inputProps, { min, max });
  }
  if (autoComplete) {
    InputProps.inputProps = Object.assign({}, InputProps.inputProps, { autoComplete });
  }
  let style;
  if (minWidth || maxWidth) {
    style = {
      minWidth: minWidth ? `${minWidth}px` : undefined,
      maxWidth: maxWidth ? `${maxWidth}px` : undefined,
    };
  }
  const textfieldType = (value !== undefined) && numeric
    ? 'number' : (type === 'password' && 'password' || undefined);
  const { displayed = true, ...userProps } = editor.componentProps(data) || {};

  return (
    <TextField
      key={editor.id}
      className={classnames(
        'grb-form-editor',
        'grb-form-editor-textfield',
        `grb-form-editor-${type}`,
        dense && 'grb-form-editor-dense',
        !displayed && 'grb-form-editor-not-displayed',
        readOnly && 'grb-readonly',
      )}
      error={!!error}
      label={label}
      value={value === undefined ? '' : value}
      type={textfieldType}
      margin={margin || (dense && 'dense') || undefined}
      onChange={handleChange}
      InputProps={InputProps}
      fullWidth={fullWidth}
      multiline={multiline || false}
      // Force vertical alignement between components with description/error and empty ones
      helperText={error || description || ''}
      id={id}
      disabled={disabled}
      style={style}
      required={required}
      {...userProps}
    />
  );
};

Component.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  editor: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.string,
    property: PropTypes.shape({
      type: PropTypes.string,
      dense: PropTypes.bool,
      description: PropTypes.string,
      multiline: PropTypes.bool,
      min: PropTypes.number,
      max: PropTypes.number,
      autoComplete: PropTypes.string,
      startAdornment: PropTypes.string,
      endAdornment: PropTypes.string,
      margin: PropTypes.string,
      fullWidth: PropTypes.bool,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      required: PropTypes.bool,
    }),
    componentProps: PropTypes.func,
  }).isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  data: PropTypes.shape({}).isRequired,
};

Component.defaultProps = {
  value: undefined,
  errors: undefined,
  disabled: undefined,
  readOnly: false,
};

class TextFieldFormEditor extends FormEditor {
  constructor(property, options) {
    super(property, options);
    this.Component = Component;
    this.componentProps = options && options.props || (() => {});
  }
}

export default TextFieldFormEditor;
